import { OVERLAY_DEFAULT_CONFIG } from '@angular/cdk/overlay';
import {
  provideHttpClient,
  withInterceptors,
  withInterceptorsFromDi,
} from '@angular/common/http';
import {
  ApplicationConfig,
  ErrorHandler,
  importProvidersFrom,
  isDevMode,
} from '@angular/core';
import { MAT_FORM_FIELD_DEFAULT_OPTIONS } from '@angular/material/form-field';
import { MatPaginatorIntl } from '@angular/material/paginator';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import {
  Router,
  provideRouter,
  withComponentInputBinding,
  withEnabledBlockingInitialNavigation,
  withRouterConfig,
} from '@angular/router';
import { provideServiceWorker } from '@angular/service-worker';
import { TailwindThemeService } from '@backoffice-frontend/shared/ui-tailwind';
import {
  LocaleProvider,
  translocoProviders,
} from '@backoffice-frontend/shared/util-transloco';
import { EnvConfig } from '@clean-code/models/common';
import { authErrorInterceptor } from '@clean-code/shared/auth/util-auth';
import { provideAzureAuth } from '@clean-code/shared/auth/util-azure';
import { MatPaginationIntlService } from '@clean-code/shared/components/ui-mat-table';
import { provideConfig } from '@clean-code/shared/util-config';
import { dateHttpInterceptor } from '@clean-code/shared/util-graphql';
import { provideCustomNativeDateAdapter } from '@clean-code/shared/util/util-date';
import {
  GlobalErrorHandlerService,
  errorInterceptorService,
} from '@clean-code/shared/util/util-error';
import {
  USER_LIMIT_SERVICE,
  UserLimitAdminService,
} from '@clean-code/user-limit/domain';
import { RxState } from '@rx-angular/state';
import * as Sentry from '@sentry/angular-ivy';
import { NgxPermissionsModule } from 'ngx-permissions';
import { provideToastr } from 'ngx-toastr';
import { environment } from '../environments/environment';
import { AppModule } from './app.module';
import { appRoutes } from './app.routes';

export const appConfig: ApplicationConfig = {
  providers: [
    providers(),
    provideCustomNativeDateAdapter(),
    // experimental, remove if not needed
    // provideExperimentalZonelessChangeDetection(),
    provideAnimationsAsync(),
    provideConfig(environment as EnvConfig),

    provideAzureAuth(environment.auth),
    importProvidersFrom(AppModule, NgxPermissionsModule.forRoot()),
    provideRouter(
      appRoutes,
      withComponentInputBinding(),
      withEnabledBlockingInitialNavigation(),
      // withDebugTracing(),
      withRouterConfig({ paramsInheritanceStrategy: 'always' }),
    ),

    provideHttpClient(
      withInterceptorsFromDi(),
      withInterceptors([
        authErrorInterceptor,
        // graphqlErrorInterceptor,
        dateHttpInterceptor,
        errorInterceptorService,
      ]),
    ),

    provideToastr(),
    translocoProviders,
    provideServiceWorker('ngsw-worker.js', {
      enabled: !isDevMode(),
      registrationStrategy: 'registerWhenStable:30000',
    }),
  ],
};

function providers() {
  return [
    RxState,
    LocaleProvider,
    TailwindThemeService,
    {
      provide: MAT_FORM_FIELD_DEFAULT_OPTIONS,
      useValue: { appearance: 'outline' },
    },
    ...(environment.enableSentry
      ? [
          {
            provide: Sentry.TraceService,
            deps: [Router],
          },
          {
            provide: ErrorHandler,
            useValue: Sentry.createErrorHandler({
              showDialog: true,
            }),
          },
        ]
      : []),
    {
      provide: ErrorHandler,
      useClass: GlobalErrorHandlerService,
    },
    {
      provide: MatPaginatorIntl,
      useClass: MatPaginationIntlService,
    },
    {
      provide: USER_LIMIT_SERVICE,
      useClass: UserLimitAdminService,
    },
    {
      provide: OVERLAY_DEFAULT_CONFIG,
      useValue: { usePopover: false },
    },
  ];
}
